<template>
  <div>
    <Error
      v-if="!showResultSummaryForm"
      class="mt-3 pl-3"
      is-alert-mode
      message="El estudiante no cuenta con un Resumen de Resultados."
    />
    <div v-if="showResultSummaryDocument">
      <div class="row mt-5">
        <div class="col-12">
          <section-title has-line title="Aprobado por Remedio Provisional" />
        </div>
        <div class="col-12">
          <custom-callout
            class-main="mt-3"
            :has-border="false"
            :has-gradient="false"
            :has-toggle="false"
          >
            <div class="row">
              <div class="col-12">
                <label class="data-name">Documento:</label>
              </div>
              <div class="col-8">
                Resumen de Resultados
              </div>
              <div class="col-4 text-right">
                <action-button
                  id="btn-download-document"
                  class="btn-action-size mr-4"
                  i="fas fa-file-download"
                  variant="success"
                  @click="printResultSummaryDocument"
                />
              </div>
            </div>
          </custom-callout>
        </div>
      </div>
    </div>
    <div v-if="showResultSummaryForm" class="mt-3">
      <validation-observer
        v-if="!showResultSummaryDocument"
        ref="resultSummary"
        novalidate
        onsubmit="return false;"
        tag="form"
      >
        <div class="row mt-5">
          <div class="col-12">
            <section-title has-line title="Núm. de años/meses en tratamiento" />
            <div
              class="row ml-1 mr-1 align-items-center border-bottom pb-3 mb-3"
            >
              <div class="col-3">
                <validation-provider v-slot="{ errors }" rules="required">
                  <custom-input
                    v-model="treatmentYearsOrMonthsValue"
                    custom-error-msg="El(los) año(s) es(son) requerido(s)."
                    :disabled="model.isSubmitted || ReadOnly"
                    :error="errors[0] ? 'err' : ''"
                    mask-type="Number"
                    :max="999"
                    :maxlength="'3'"
                    :min="0"
                    :name="treatmentYearsOrMonthsValue"
                    style="max-width: 225px"
                  />
                </validation-provider>
              </div>
              <div class="col d-flex justify-content-start">
                <validation-provider v-slot="{ errors }" rules="required">
                  <custom-radio
                    v-model="treatmentYearsOrMonths.value"
                    custom-error-msg="Favor seleccione"
                    :is-disabled="model.isSubmitted || ReadOnly"
                    :error="Boolean(errors[0])"
                    :is-inline="true"
                    name="rbTreatment"
                    :options="treatmentOptions"
                  />
                </validation-provider>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-12">
            <section-title
              has-line
              title="La fecha de la primera evaluación realizada que recomendó el servicio"
            />

            <evaluation-row
              add-title="Presione aquí para añadir evaluación"
              custom-error-msg="Favor seleccionar la evaluación."
              date-title="Fecha de Evaluación:"
              :default-date="catalog.dateOptions.evaluationDate"
              :disabled="model.isSubmitted || ReadOnly"
              :first-item="model.evaluation.id == 0"
              :info="model.evaluation"
              @save="saveEvaluationHandler"
            />

            <div v-if="validate">
              <error
                v-if="errors.evaluation.invalid"
                :message="errors.evaluation.message"
                variant="no-border"
              />
            </div>
          </div>
        </div>

        <div class="row mt-5">
          <div class="col-12">
            <section-title
              has-line
              title="Si ha sido reevaluado, precise la información a continuación para cada una de las veces en que fuera evaluado"
            />
            <div
              v-for="(reevaluation, idx) in model.reEvaluations"
              :key="`reevaluation-${idx}`"
              class="mb-2"
            >
              <evaluation-row
                add-title="Presione aquí para añadir reevaluación"
                confirm-date-title="Confirmar Fecha de Reevaluación:"
                custom-error-msg="Favor seleccionar la reevaluación."
                :date-options="availableReevaluationDates(reevaluation.date)"
                date-title="Fecha de Reevaluación:"
                :disabled="model.isSubmitted || ReadOnly"
                :first-item="reevaluation.id == 0"
                :info="reevaluation"
                re-evaluation
                @cancel="cancelReevaluationHandler(idx)"
                @save="saveReevaluationHandler($event, idx)"
              />
            </div>
            <div
              v-if="showAddEvaluationBtn && !model.isSubmitted && !ReadOnly"
              class="w-100 text-right"
            >
              <action-button
                i="fas fa-file-plus"
                text="Añadir"
                variant="success mt-2"
                @click="addReevaluationHandler"
              />
            </div>
          </div>
        </div>

        <div class="row mt-5">
          <div class="col-12">
            <section-title
              has-line
              title="Desglose las metas a largo plazo desarrolladas en los últimos planes de intervención y el nivel de ejecución alcanzado (logros) en cada uno de los años señalados."
            />
            <long-term-goals
              :academic-years="catalog.academicYears"
              :disabled="model.isSubmitted || ReadOnly"
              :execution-level-archieved="catalog.executionLevelArchieved"
              :goals="model.goals"
              @alert="alertHandler"
            />
            <div v-if="validate">
              <error
                v-if="errors.goals.invalid"
                :message="errors.goals.message"
                variant="no-border"
              />
              <error
                v-if="goalsCountErrorMessage"
                :message="goalsCountErrorMessage"
                variant="no-border"
              />
            </div>
          </div>
        </div>

        <div class="row mt-5">
          <div class="col-12">
            <section-title
              has-line
              title="Resuma los resultados de intervención"
            />
            <div class="ml-3">
              <validation-provider v-slot="{ errors }" rules="required">
                <custom-text-area
                  v-model="model.resume"
                  class="mb-2"
                  :count-classes="['d-block text-right']"
                  custom-error-msg="Favor resuma los resultados."
                  :disabled="model.isSubmitted || ReadOnly"
                  :error="errors[0] ? 'err' : ''"
                  label="Resuma los resultados:"
                  :maxlength="3000"
                  :show-char-count="true"
                />
              </validation-provider>
              <validation-provider v-slot="{ errors }" rules="required">
                <div>
                  <span class="color-gray-6 font-14 font-weight-normal">
                    Especifique si:
                  </span>

                  <custom-radio
                    v-model="model.resumeType.value"
                    custom-error-msg="Favor especificar."
                    :is-disabled="model.isSubmitted || ReadOnly"
                    :error="Boolean(errors[0])"
                    :is-inline="false"
                    name="rbInterventionResume"
                    :options="interventionResumeOptions"
                  />
                </div>
              </validation-provider>
            </div>
          </div>
        </div>

        <div class="row mt-5">
          <div class="col-12">
            <section-title
              has-line
              title="Precise las fortalezas del estudiante"
            />
            <div class="bg-secondary-13 p-3 rounded text-center ml-3">
              <div v-if="!isAddStrengthMode && !ReadOnly">
                <action-button
                  class="border btn px-5"
                  :disabled="model.isSubmitted"
                  i="fas fa-file-plus color-green"
                  text="Presione aquí para añadir fortalezas"
                  @click="isAddStrengthMode = true"
                />
                <custom-chips-group
                  v-if="catalog.addedStrengths.length"
                  class="text-left"
                  :disabled="model.isSubmitted || ReadOnly"
                  no-background
                  no-wrap
                  object-text="name"
                  :on-item-deleted="onDeleteStrengthHandler"
                  :selected-items="catalog.addedStrengths"
                  title="Fortaleza(s) seleccionada(s)"
                />
              </div>
              <div v-if="isAddStrengthMode">
                <multi-selection-picker
                  add-item-title-label="Añadir nueva Fortaleza a su listado"
                  :all-items="getAvailableStrengths"
                  allow-item-list-editing
                  class="text-left"
                  :column-count="1"
                  :disabled="model.isSubmitted || ReadOnly"
                  edit-item-title-label="Editar Fortaleza de su listado"
                  id-prop-name="id"
                  item-type-name="fortaleza"
                  maxlength="100"
                  modify-removes-item
                  placeholder="Escriba aquí para buscar una"
                  text-prop-name="name"
                  @itemAdded="onAddedStrengthHandler"
                  @itemModified="onModifiedStrengthHandler"
                  @itemSelected="onSelectedStrengthHandler"
                />
                <custom-chips-group
                  v-if="catalog.addedStrengths.length"
                  class="text-left"
                  :disabled="model.isSubmitted || ReadOnly"
                  no-background
                  no-wrap
                  object-text="name"
                  :on-item-deleted="onDeleteStrengthHandler"
                  :selected-items="catalog.addedStrengths"
                  title="Fortaleza(s) seleccionada(s)"
                />

                <div
                  v-if="!ReadOnly"
                  class="flex-grow-1 d-flex align-items-center justify-content-end pt-4"
                >
                  <action-button
                    class="px-3 mr-2"
                    :disabled="model.isSubmitted"
                    i="fas fa-times-circle"
                    text="Cancelar"
                    variant="outline-danger"
                    @click="isAddStrengthMode = false"
                  />
                  <action-button
                    class="px-3"
                    :disabled="model.isSubmitted"
                    i="fas fa-arrow-alt-circle-right"
                    text="Continuar"
                    variant="success"
                    @click="onStrengthContinue"
                  />
                </div>
              </div>
            </div>
            <div v-if="validate">
              <error
                v-if="errors.strengths.invalid"
                :message="errors.strengths.message"
                variant="no-border"
              />
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-12 mt-5">
            <section-title
              has-line
              title="Señale los factores que podrían estar afectando el progreso del estudiante en el proceso de intervención"
            />
            <div class="ml-3">
              <div v-for="factor in studentFactorsOptions" :key="factor.id">
                <check-box
                  v-model="factor.check"
                  :disabled="model.isSubmitted || ReadOnly"
                  name="studentFactors"
                  :text="factor.label"
                />
              </div>
            </div>

            <div v-if="showStudentFactorsOthers" class="ml-5">
              <div
                v-for="factor in studentFactorsOthersOptions"
                :key="factor.id"
              >
                <check-box
                  v-model="factor.check"
                  :disabled="model.isSubmitted || ReadOnly"
                  name="studentOtherFactors"
                  :text="factor.label"
                />
              </div>
              <div v-if="isCustomFactorSelected" class="ml-5">
                <validation-provider v-slot="{ errors }" rules="required">
                  <custom-text-area
                    v-model="model.otherFactor"
                    :count-classes="['d-block text-right']"
                    custom-error-msg="Favor llenar este campo."
                    :disabled="model.isSubmitted || ReadOnly"
                    :error="errors[0] ? 'err' : ''"
                    :maxlength="3000"
                    :show-char-count="true"
                  />
                </validation-provider>
              </div>
            </div>

            <error
              v-if="studentFactorHasError"
              message="Debe seleccionar un factor."
              variant="no-border"
            />
          </div>
        </div>

        <div class="row mt-5">
          <div class="col-12">
            <section-title
              has-line
              title="Si considera que el estudiante requiere continuar en servicio, establezca la necesidad del mismo indicando la meta hacia la que dirigirá las intervenciones, aspectos que está considerando sobre capacidad recuperativa, un estimado de tiempo adicional recomendado y la modalidad."
            />
            <div class="ml-3">
              <validation-provider v-slot="{ errors }" rules="required">
                <div class="border-bottom py-2">
                  <custom-radio
                    v-model="requiresContinueService.value"
                    custom-error-msg="Favor seleccione"
                    :is-disabled="model.isSubmitted || ReadOnly"
                    :error="Boolean(errors[0])"
                    :is-inline="false"
                    name="rbRequiresContinueService"
                    :options="requiresContinueServiceOptions"
                  />
                </div>
              </validation-provider>
              <div v-if="showContinueService" class="mt-3">
                <validation-provider v-slot="{ errors }" rules="required">
                  <custom-text-area
                    v-model="continuityServiceComment"
                    :count-classes="['d-block text-right']"
                    custom-error-msg="El campo es requerido"
                    :error="errors[0] ? 'err' : ''"
                    :maxlength="3000"
                    :show-char-count="true"
                    :disabled="!ReadOnly"
                  />
                </validation-provider>
                <validation-provider v-slot="{ errors }" rules="required">
                  <div class="color-gray-6 font-14 font-weight-normal">
                    Término recomendado:
                  </div>
                  <custom-radio
                    v-model="recomendedTerm.value"
                    custom-error-msg="Favor seleccione"
                    :is-disabled="model.isSubmitted || ReadOnly"
                    :error="Boolean(errors[0])"
                    is-inline
                    name="rbRecomendedTerm"
                    :options="recomendedTermOptions"
                  />
                </validation-provider>
              </div>
              <div v-if="showContinueService" class="mt-3">
                <validation-provider v-slot="{ errors }" rules="required">
                  <div class="color-gray-6 font-14 font-weight-normal">
                    La capacidad recuperativa del estudiante se estima:
                  </div>
                  <custom-radio
                    v-model="recoveryStudentCapacity.value"
                    custom-error-msg="Favor seleccione"
                    :is-disabled="model.isSubmitted || ReadOnly"
                    :error="Boolean(errors[0])"
                    is-inline
                    name="rbRecoveryStudentCapacity"
                    :options="recoveryStudentCapacityOptions"
                  />
                </validation-provider>
              </div>
            </div>
          </div>
        </div>

        <div class="row mt-5">
          <div class="col-12">
            <section-title
              has-line
              title="Análisis de resultado de intervención"
            />
            <div class="ml-4 pl-2">
              <div class="row mt-5">
                <div class="col-12">
                  <div
                    class="row ml-1 mr-1 align-items-center border-bottom pb-3 mb-3"
                  >
                    <div class="col ">
                      <validation-provider v-slot="{ errors }" rules="required">
                        <div class="d-flex align-items-center">
                          <span
                            class="color-gray-6 font-16 font-weight-normal mr-2"
                          >
                            Nos recomienda por los pasados:
                          </span>
                          <custom-input
                            v-model="yearsOrMonthsValue"
                            custom-error-msg="El(los) año(s) es(son) requerido(s)."
                            :disabled="model.isSubmitted || ReadOnly"
                            :error="errors[0] ? 'err' : ''"
                            style="max-width: 225px"
                          />
                        </div>
                      </validation-provider>
                    </div>
                    <div class="col d-flex justify-content-start">
                      <validation-provider v-slot="{ errors }" rules="required">
                        <custom-radio
                          v-model="yearsOrMonths.value"
                          custom-error-msg="Favor seleccione"
                          :is-disabled="model.isSubmitted || ReadOnly"
                          :error="Boolean(errors[0])"
                          :is-inline="true"
                          name="rbRecommended"
                          :options="recommendedOptions"
                        />
                      </validation-provider>
                    </div>
                  </div>
                </div>
              </div>

              <div class="ml-3">
                <div class="border-bottom py-2">
                  <check-box
                    v-model="model.analysis.focusInGoalService"
                    class="my-2"
                    :disabled="model.isSubmitted || ReadOnly"
                    text="Continuar en el servicio, enfocando el servicio hacia la meta indicada arriba."
                  />
                </div>
                <div class="border-bottom py-2">
                  <div class="row">
                    <div class="col">
                      <check-box
                        v-model="model.analysis.frequencyTypeCheck"
                        class="my-2"
                        :disabled="model.isSubmitted || ReadOnly"
                        text="Continuar en el servicio y disminuir la frecuencia a:"
                      />
                    </div>
                    <div class="col d-flex justify-content-end pr-5">
                      <custom-drop-down
                        v-model="model.analysis.frequencyTypeId"
                        class="w-50"
                        :custom-error-msg="errors.analysisFrequencyType.message"
                        :disabled="model.isSubmitted || ReadOnly"
                        :error="
                          validate && errors.analysisFrequencyType.invalid
                            ? errors.analysisFrequencyType.message
                            : ''
                        "
                        fieldkey="id"
                        fieldname="name"
                        :options="catalog.frequencyTypes"
                        placeholder="Seleccione"
                        @input="
                          model.analysis.frequencyTypeCheck = !!model.analysis
                            .frequencyTypeId
                        "
                      />
                    </div>
                  </div>
                </div>
                <div class="border-bottom py-2">
                  <div class="row">
                    <div class="col">
                      <check-box
                        v-model="model.analysis.serviceModalityCheck"
                        class="my-2"
                        :disabled="model.isSubmitted || ReadOnly"
                        text="Continuar en el servicio en una modalidad diferente:"
                      />
                    </div>
                    <div class="col d-flex justify-content-end pr-5">
                      <custom-drop-down
                        v-model="model.analysis.serviceModalityId"
                        class="w-50"
                        :custom-error-msg="
                          errors.analysisServiceModality.message
                        "
                        :disabled="model.isSubmitted || ReadOnly"
                        :error="
                          validate && errors.analysisServiceModality.invalid
                            ? errors.analysisServiceModality.message
                            : ''
                        "
                        fieldkey="id"
                        fieldname="name"
                        :options="catalog.serviceModalities"
                        placeholder="Seleccione"
                        @input="
                          model.analysis.serviceModalityCheck = !!model.analysis
                            .serviceModalityId
                        "
                      />
                    </div>
                  </div>
                </div>
                <div class="border-bottom py-2">
                  <div class="row">
                    <div class="col">
                      <check-box
                        v-model="model.analysis.periodCheck"
                        class="my-2"
                        :disabled="model.isSubmitted || ReadOnly"
                        text="Comenzar y completar el alta en un periodo estimado de:"
                      />
                    </div>
                    <div class="col d-flex justify-content-end pr-5">
                      <custom-input
                        v-model="model.analysis.period"
                        class="pr-3"
                        :custom-error-msg="errors.analysisPeriodAmount.message"
                        :disabled="model.isSubmitted || ReadOnly"
                        :error="
                          validate && errors.analysisPeriodAmount.invalid
                            ? errors.analysisPeriodAmount.message
                            : ''
                        "
                        style="max-width: 225px"
                        @input="
                          model.analysis.periodCheck =
                            !!model.analysis.period ||
                            !!model.analysis.periodTypeId
                        "
                      />
                      <custom-drop-down
                        v-model="model.analysis.periodTypeId"
                        class="w-50"
                        :custom-error-msg="errors.analysisPeriod.message"
                        :disabled="model.isSubmitted || ReadOnly"
                        :error="
                          validate && errors.analysisPeriod.invalid
                            ? errors.analysisPeriod.message
                            : ''
                        "
                        fieldkey="id"
                        fieldname="name"
                        :options="catalog.periods"
                        placeholder="Seleccione"
                        @input="
                          model.analysis.periodCheck =
                            !!model.analysis.period ||
                            !!model.analysis.periodTypeId
                        "
                      />
                    </div>
                  </div>
                </div>
                <div class="border-bottom py-2">
                  <check-box
                    v-model="model.analysis.focusInObjectiveService"
                    class="my-2"
                    :disabled="model.isSubmitted || ReadOnly"
                    text="Continuar el servicio con plan que incluya objetivos hacia el alta de la intervención."
                  />
                </div>
                <div class="border-bottom py-2">
                  <check-box
                    v-model="model.analysis.reevaluationForUpdates"
                    class="my-2"
                    :disabled="model.isSubmitted || ReadOnly"
                    text="Reevaluar para actualizar datos que abonen al curso del tratamiento.."
                  />
                </div>
              </div>
              <div v-if="validate">
                <error
                  v-if="errors.analysis.invalid"
                  :message="errors.analysis.message"
                  variant="no-border"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="row mt-5 ml-2 mb-5">
          <section-title has-line title="Firma de Especialista" />
          <div class="col-12 ml-2">
            <SpecialistInformationWriter
              v-model="specialistModel"
              :disabled="model.isSubmitted || ReadOnly"
              :service-specialists="serviceSpecialists"
              :writer-specialists="writerSpecialists"
              @getSpecialistDetail="getSpecialistDetail"
            />
          </div>
        </div>
      </validation-observer>

      <validation-observer
        ref="resultSummaryConfirmation"
        novalidate
        onsubmit="return false;"
        tag="form"
      >
        <div class="row mt-5 ml-2 mb-5">
          <title-breadcrumb :page="certificationTitle" :has-route="false" />
          <div class="col-12 px-0">
            <div class="col-12 px-1">
              <div class="border-box">
                Certifico por este medio, que el COMPU ha leído en todas sus
                partes el resumen de resultados
              </div>
            </div>
            <div class="col-12 px-0">
              <validation-provider
                v-slot="{ errors }"
                name="checkboxConditions"
                :rules="{ required: { allowFalse: false } }"
              >
                <div class="row mx-1">
                  <div
                    class="border-box gray"
                    :class="{ error: errors.length > 0 }"
                  >
                    <div class="col-12 px-0 py-0">
                      <div class="custom-control custom-checkbox">
                        <div class="custom-control custom-checkbox">
                          <input
                            id="checkboxConditions"
                            v-model="model.certifySummary"
                            type="checkbox"
                            class="custom-control-input"
                            name="checkboxConditions"
                            :disabled="ReadOnly"
                            @change="certifyResultSummary"
                          />
                          <label
                            class="custom-control-label pl-3"
                            for="checkboxConditions"
                          >
                            Leído
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-if="errors.length > 0" class="row mt-1">
                    <div class="col-12">
                      <i class="fa fa-times-circle text-red pr-2" />
                      <span class="invalid">
                        Favor certificar el resumen de resultados.
                      </span>
                    </div>
                  </div>
                </div>
              </validation-provider>
            </div>
          </div>
        </div>

        <div class="row mt-5 ml-2 mb-5">
          <section-title has-line title="Recomendación del Especialista" />
          <div class="col-12">
            <div>
              <label class="data-name font-16"
                >¿Se acoge a la recomendación del especialista?</label
              >
            </div>
            <validation-provider v-slot="{ errors }" rules="required">
              <custom-radio
                v-model="specialistRecommendation.value"
                class="col-12"
                custom-error-msg="Este campo debe ser completado."
                :error="Boolean(errors[0])"
                :is-inline="true"
                name="rbSpecialistRecommendation"
                :options="specialistRecommendationOptions"
                :change-event="onSpecialistRecommendationChange"
                :is-disabled="ReadOnly"
              />
            </validation-provider>
          </div>
        </div>
        <div v-if="showJustificationSection" class="row mt-5 ml-2 mb-5">
          <div class="col-12">
            <div>
              <label class="data-name font-16"
                >Justificación del COMPU para no acogerse a la recomendación del
                especialista:</label
              >
            </div>
            <validation-provider v-slot="{ errors }" rules="required">
              <custom-text-area
                v-model="referralJustificationComment"
                class="mb-2"
                :count-classes="['d-block text-right']"
                custom-error-msg="Favor agregar la justificación."
                :error="errors[0] ? 'err' : ''"
                :maxlength="3000"
                :show-char-count="true"
                :disabled="ReadOnly"
              />
            </validation-provider>
          </div>
        </div>
      </validation-observer>

      <div
        v-if="!ReadOnly"
        class="mt-3 d-flex justify-content-end align-items-center"
      >
        <ActionButton i="fas fa-save" variant="success m-2" @click="save">
          Guardar
        </ActionButton>
      </div>
    </div>
  </div>
</template>

<script>
import SectionTitle from "SectionTitle";
import ActionButton from "ActionButton";
import CustomInput from "CustomInput";
import CustomRadio from "custom-radio";
import CustomTextArea from "CustomTextArea";
import CustomDropDown from "custom-drop-down";
import CheckBox from "CheckBox";
import CustomChipsGroup from "CustomChipsGroup";
import MultiSelectionPicker from "@/components/common/MultiSelectionPicker";
import EvaluationRow from "./EvaluationRow.vue";
import LongTermGoals from "./LongTermGoals.vue";
import SpecialistInformationWriter from "./SpecialistInformationWriter.vue";
import TitleBreadcrumb from "@/components/common/page/TitleBreadcrumb";
import Error from "@/components/common/Error";
import CustomCallout from "custom-callout";

import {
  getSpecialistInfo as _getSpecialistInfo,
  getStudentReferralAdmisionBySie,
  getCodeExecutionLevelArchieved,
  getEvaluationDates,
  getServiceResultSummary,
  getServiceResultSummaryHistory,
  certifyServiceResultSummary,
  getSpecialistsForAssignment,
  specialistsList as getSpecialistList,
  saveResultSummarySpecialistRecommendation,
  getResultSummaryForRP
} from "@/modules/referral/api";
import codeServiceManager from "@/modules/common/code.api.js";
import { getPeriods as _getPeriods } from "@/modules/common/code.api";
import { getAcademicYearsToType } from "@/services/api/academicUtils.api.js";
import generalService from "@/services/pei/general";
import codeQuestionAnswers from "@/utils/constants/codeQuestionAnswers";
import admissionStatus from "@/utils/constants/pei/referral/admissionStatus.js";
import DocumentsService from "@/services/documents";

import store from "@/store/index";
import alertMixin from "@/mixin/alert.mixin";
import moment from "moment";

const initialModel = () => ({
  otherFactor: "",
  signed: null,
  evaluation: {
    id: 0,
    name: "",
    isFirstEvaluation: true
  },
  analysis: {
    years: null,
    months: null,
    focusInGoalService: false,
    frequencyTypeId: null,
    frequencyTypeCheck: null,
    serviceModalityId: null,
    serviceModalityCheck: null,
    periodTypeId: null,
    period: null,
    periodCheck: null,
    focusInObjectiveService: false,
    reevaluationForUpdates: false,
    requiresContinueService: { id: null }
  },
  goals: [],
  reEvaluations: [{ id: 0 }],
  therapyDuration: null,
  specialistSignature: [],
  isSubmitted: true,
  strengths: [],
  resumeType: { id: null },
  resume: null,
  summaryHistory: [],
  certifySummary: null,
  originalSpecialistRecommendation: null,
  originalJustificationComment: null
});

export default {
  name: "ResultSummary",
  mixins: [alertMixin],
  components: {
    SectionTitle,
    ActionButton,
    CustomInput,
    CustomRadio,
    CustomTextArea,
    CustomDropDown,
    CheckBox,
    CustomChipsGroup,
    MultiSelectionPicker,
    EvaluationRow,
    LongTermGoals,
    SpecialistInformationWriter,
    TitleBreadcrumb,
    Error,
    CustomCallout
  },
  data() {
    return {
      model: initialModel(),
      catalog: {
        dateOptions: {
          evaluationDate: null,
          reEvaluationDate: [],
          reEvaluationDateOriginal: []
        },
        academicYears: [],
        strengths: [],
        addedStrengths: [],
        periods: [],
        frequencyTypes: [],
        serviceModalities: [],
        executionLevelArchieved: []
      },
      flags: {
        addEvaluationMode: false
      },
      specialistModel: {
        selectedServiceSpecialist: {},
        serviceSpecialist: null,
        writerSpecialist: null
      },
      admissionInfo: {},
      serviceSpecialists: [],
      writerSpecialists: [],
      treatmentYearsOrMonths: { id: 1, value: "Años" },
      treatmentYearsOrMonthsValue: null,
      validate: false,
      goalsCountErrorMessage: null,
      isAddStrengthMode: null,
      studentFactorsOptions: [],
      studentFactorsOthersOptions: [],
      studentFactorHasError: false,
      continuityServiceComment: null,
      recomendedTerm: { id: null },
      recoveryStudentCapacity: { id: null },
      requiresContinueService: { id: null },
      yearsOrMonths: { id: null },
      yearsOrMonthsValue: null,
      certificationTitle: {
        icon: "far fa-check-circle",
        title: "Certificación de Resumen de Resultados"
      },
      specialistRecommendation: { id: null },
      referralJustificationComment: null,
      peiAnalysisService: null,
      showResultSummaryDocument: false,
      hasResultSummaryForm: true,
      validationTriggered: store.getters.getPeiValidationTriggered,
      treatmentOptions: [
        {
          id: 1,
          name: "Años",
          value: 1,
          label: "Años",
          check: false,
          invalid: false
        },
        {
          id: 2,
          name: "Meses",
          value: 2,
          label: "Meses ",
          check: false,
          invalid: false
        }
      ],
      interventionResumeOptions: [
        {
          id: 1,
          name: "1",
          value: 1,
          label: "Es admisión o traslado reciente.",
          check: false,
          invalid: false
        },
        {
          id: 2,
          name: "2",
          value: 2,
          label: "Hubo cambio reciente de especialista.",
          check: false,
          invalid: false
        },
        {
          id: 3,
          name: "3",
          value: 3,
          label:
            "Se ha manifestado pérdida de comunicación con padre/madre/encargado.",
          check: false,
          invalid: false
        },
        {
          id: 4,
          name: "4",
          value: 4,
          label: "Ninguna de las anteriores.",
          check: false,
          invalid: false
        }
      ],
      requiresContinueServiceOptions: [
        {
          id: 393,
          name: "393",
          value: 393,
          label:
            "No aplica, no se recomienda que el estudiante continúe con el servicio.",
          check: false,
          invalid: false
        },
        {
          id: 394,
          name: "394",
          value: 394,
          label:
            "Se recomienda que el estudiante continúe en servicio enfocado",
          check: false,
          invalid: false
        }
      ],
      recommendedOptions: [
        {
          id: 1,
          name: "Años",
          value: 1,
          label: "Años",
          check: false,
          invalid: false
        },
        {
          id: 2,
          name: "Meses",
          value: 2,
          label: "Meses ",
          check: false,
          invalid: false
        }
      ],
      recomendedTermOptions: [
        {
          id: 395,
          name: "395",
          value: 395,
          label: "1 año adicional",
          check: false,
          invalid: false
        },
        {
          id: 396,
          name: "396",
          value: 396,
          label: "1 a 2 años adicionales",
          check: false,
          invalid: false
        },
        {
          id: 397,
          name: "397",
          value: 397,
          label: "2 a 3 años adicionales",
          check: false,
          invalid: false
        }
      ],
      recoveryStudentCapacityOptions: [
        {
          id: 398,
          name: "398",
          value: 398,
          label: "Favorable",
          check: false,
          invalid: false
        },
        {
          id: 399,
          name: "399",
          value: 399,
          label: "Desfavorable",
          check: false,
          invalid: false
        },
        {
          id: 400,
          name: "400",
          value: 400,
          label: "Condicionada",
          check: false,
          invalid: false
        },
        {
          id: 401,
          name: "401",
          value: 401,
          label: "Reservada",
          check: false,
          invalid: false
        }
      ],
      specialistRecommendationOptions: [
        {
          id: 1,
          name: "Si",
          value: true,
          label: "Si",
          check: false,
          invalid: false
        },
        {
          id: 2,
          name: "No",
          value: false,
          label: "No",
          check: false,
          invalid: false
        }
      ],
      hasStoredRecommendationValue: false
    };
  },
  props: {
    hasResultSummary: {
      type: Boolean,
      default: true
    },
    studentSie: {
      type: Number,
      default: null
    },
    academicYearId: {
      type: Number,
      default: null
    },
    disciplineTypeId: {
      type: Number,
      default: null
    },
    disciplineName: {
      type: String,
      default: null
    },
    programServiceAreaId: {
      type: [Number, String],
      default: null
    },
    peiId: {
      type: String,
      default: null
    },
    admissionStatusId: {
      type: Number,
      default: null
    },
    resultSummaryDocument: {
      type: Object,
      default: null
    },
    ReadOnly: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    hasResultSummary(newVal) {
      if (newVal) {
        this.loadData();
      }
    },
    specialistRecommendation: {
      deep: true,
      handler(newValue, oldValue) {
        if (oldValue?.id == null && this.hasStoredRecommendationValue) return;

        this.$emit(
          "pending-changes",
          this.originalSpecialistRecommendation?.id != newValue?.id
        );
      }
    },
    referralJustificationComment: {
      deep: true,
      handler(newValue) {
        this.$emit(
          "pending-changes",
          this.originalJustificationComment != newValue
        );
      }
    }
  },
  computed: {
    validator() {
      return store.getters.tab4Validator;
    },
    showResultSummaryForm() {
      return this.hasResultSummaryForm && this.hasResultSummary;
    },
    continueServiceRecommended() {
      return (
        this.requiresContinueService &&
        this.requiresContinueService.id &&
        this.requiresContinueService.value ===
          codeQuestionAnswers.SeRecomiendaContinueServicio
      );
    },
    specialistRecommendationAccepted() {
      return (
        this.specialistRecommendation &&
        this.specialistRecommendation.id != null &&
        this.specialistRecommendation.value
      );
    },
    showJustificationSection() {
      return this.specialistRecommendation.value === false;
    },
    factorQuestionIdNamedOther() {
      return 740; // JSF: question id for showing the factor's section below "[x] Otros"
    },
    errors() {
      return {
        evaluation: {
          invalid: !this.model.evaluation.diagnosis,
          message: "Favor documentar la fecha de la primera evaluación."
        },
        goals: {
          invalid: this.model.goals == 0,
          message: "Favor desglozar las metas a largo plazo."
        },
        strengths: {
          invalid: this.model.strengths == 0,
          message: "Favor precisar las fortalezas."
        },
        analysis: {
          invalid:
            !this.model.analysis.focusInGoalService &&
            !this.model.analysis.frequencyTypeId &&
            !this.model.analysis.serviceModalityId &&
            !this.model.analysis.periodTypeId &&
            !this.model.analysis.focusInObjectiveService &&
            !this.model.analysis.reevaluationForUpdates,
          message: "Favor seleccione al menos una opción."
        },
        analysisFrequencyType: {
          invalid:
            this.model.analysis.frequencyTypeCheck &&
            !this.model.analysis.frequencyTypeId,
          message: "La frecuencia es requerida."
        },
        analysisServiceModality: {
          invalid:
            this.model.analysis.serviceModalityCheck &&
            !this.model.analysis.serviceModalityId,
          message: "La modaliad es requerida."
        },
        analysisPeriodAmount: {
          invalid:
            this.model.analysis.periodCheck && !this.model.analysis.period,
          message: "La cantidad es requerida."
        },
        analysisPeriod: {
          invalid:
            this.model.analysis.periodCheck &&
            !this.model.analysis.periodTypeId,
          message: "El periodo es requerido."
        }
      };
    },
    admissionId() {
      return this.admissionInfo?.admissionId;
    },
    admissionDisciplineTypeId() {
      return this.admissionInfo?.disciplineTypeId;
    },
    availableEvaluationDates() {
      return this.catalog.dateOptions.reEvaluationDate.filter(date =>
        this.model.reEvaluations.every(r => {
          const result = this.isObject(r.date) ? r.date.name : r.date;
          return result != date.name;
        })
      );
    },
    showAddEvaluationBtn() {
      return (
        this.availableEvaluationDates.length &&
        this.model.reEvaluations[0].diagnosis &&
        !this.flags.addEvaluationMode
      );
    },
    showStudentFactorsOthers() {
      return this.studentFactorsOptions.some(
        x => x.id === this.factorQuestionIdNamedOther && x.check
      );
    },
    isCustomFactorSelected() {
      return (
        this.showStudentFactorsOthers &&
        this.studentFactorsOthersOptions.some(
          x => x.name === "custom-factor-field" && x.check
        )
      );
    },
    showContinueService() {
      return this.requiresContinueService
        ? this.requiresContinueService.id == 394
        : false;
    }
  },
  methods: {
    saveEvaluationHandler(evaluation) {
      this.model.evaluation = evaluation;
    },
    availableReevaluationDates(currentDate) {
      currentDate = moment(currentDate).isValid()
        ? this.formatDate(currentDate)
        : currentDate;

      return this.catalog.dateOptions.reEvaluationDate.filter(date =>
        this.model.reEvaluations.every(r => {
          const result = this.isObject(r.date) ? r.date.name : r.date;
          return (
            result != this.formatDate(date.default) ||
            this.formatDate(date.default) == currentDate
          );
        })
      );
    },
    cancelReevaluationHandler(idx) {
      this.model.reEvaluations.length == 1
        ? (this.model.reEvaluations = [{}])
        : this.model.reEvaluations.splice(idx, 1);
      this.flags.addEvaluationMode = false;
    },
    saveReevaluationHandler(reevaluation, idx) {
      const newReevaluation = { ...reevaluation };
      newReevaluation.date = newReevaluation.date.name || newReevaluation.date;
      this.model.reEvaluations.splice(idx, 1, newReevaluation);
      this.flags.addEvaluationMode = false;
    },
    onDeleteStrengthHandler(item) {
      this.catalog.addedStrengths.splice(
        this.catalog.addedStrengths.indexOf(item),
        1
      );
    },
    getAvailableStrengths() {
      const selected = this.catalog.addedStrengths.map(x => {
        return x.name;
      });
      return this.catalog.strengths.filter(x => !selected.includes(x.name));
    },
    onAddedStrengthHandler(itemText) {
      this.catalog.strengths.push({
        id: (this.idCounterStrength += -1),
        name: itemText
      });
    },
    onModifiedStrengthHandler(item, index) {
      if (item.id > 0) {
        item.isDirty = true;
      }
      this.catalog.strengths.splice(index, 0, item);
    },
    onSelectedStrengthHandler(item) {
      this.catalog.addedStrengths.push({
        id: item.id,
        name: item.name,
        highlight: true
      });
    },
    async getServiceModalities() {
      const { data } = await codeServiceManager.getServiceModalities({
        forTh: true
      });
      this.catalog.serviceModalities = data;
    },
    async getPeriods() {
      const { data } = await _getPeriods();
      this.catalog.periods = data;
    },
    async getFrequencyType() {
      const { data } = await codeServiceManager.getFrequencyType();
      this.catalog.frequencyTypes = data;
    },
    async getAdmissionInfo() {
      const { data } = await getStudentReferralAdmisionBySie(
        this.studentSie,
        this.disciplineTypeId,
        this.academicYearId
      );
      this.admissionInfo = { ...data };
      await this.getSummary();
    },
    async getCodeExecutionLevelArchieved() {
      const { data } = await getCodeExecutionLevelArchieved();
      this.catalog.executionLevelArchieved = data;
    },
    async getAcademicYearsToType() {
      const { data } = await getAcademicYearsToType("CURRENT", 2018);
      this.catalog.academicYears = data;
    },
    async getEvaluationDates() {
      const { data } = await getEvaluationDates(
        this.admissionInfo.studentSieId
      );
      this.catalog.dateOptions = {
        evaluationDate: data.initialEvaluationDate,
        reEvaluationDate: data.additionalEvaluationDates.map((date, idx) => ({
          id: idx,
          name: this.formatDate(date),
          default: date
        }))
      };
    },
    async getSpecialists() {
      const isAdmission = false;
      const specialistId = 0;
      //Services Specilists
      const { data: serviceSpecialists } = await getSpecialistsForAssignment(
        this.admissionId,
        this.admissionInfo.locationFiscalYearId,
        this.admissionInfo.studentSieId,
        specialistId,
        isAdmission
      );

      this.serviceSpecialists = serviceSpecialists;

      //Plan's Writer
      const { data: writerSpecialists } = await getSpecialistList({
        fiscalYearId: this.admissionInfo.academicYearId,
        providerId: this.admissionInfo.companyId,
        disciplineTypeId: this.admissionInfo.disciplineTypeId,
        validateUserProfile: false, //para no evaluar el perfil de usuario.
        validateLicense: true
      });

      this.writerSpecialists = writerSpecialists;
    },
    async getSpecialistDetail() {
      const isSpecialistServiceSelected =
        this.specialistModel &&
        this.specialistModel.serviceSpecialist &&
        this.specialistModel.serviceSpecialist.id > 0;
      if (!isSpecialistServiceSelected) {
        this.specialistModel.selectedServiceSpecialist = {
          fullName: "",
          licenceNo: "",
          expirationDate: ""
        };
        return;
      }

      const { data: specialistDetail } = await _getSpecialistInfo(
        this.specialistModel.serviceSpecialist.id,
        this.admissionInfo.disciplineTypeId
      );

      this.specialistModel.selectedServiceSpecialist = specialistDetail;
      this.specialistModel.selectedServiceSpecialist.expirationDate = specialistDetail?.expirationDate
        ? this.formatDate(specialistDetail?.expirationDate)
        : "";
    },
    async getSummary() {
      const { data } = await getServiceResultSummary({
        studentSieId: this.admissionInfo.studentSieId,
        studentServiceId: this.admissionInfo.studentServiceId,
        academicYearId: this.admissionInfo.academicYearId,
        interventionServiceResultSummaryId: this
          .interventionServiceResultSummaryId,
        disciplineTypeId: this.admissionDisciplineTypeId,
        programServiceAreaId: Number(this.programServiceAreaId),
        submittedOnly: true
      });

      if (!data || data.id == null) {
        this.hasResultSummaryForm = false;
        return;
      }

      this.specialistModel.serviceSpecialist = data.serviceSpecialist;
      this.specialistModel.writerSpecialist = data.writerSpecialist;

      if (this.specialistModel.serviceSpecialist == null) {
        this.specialistModel.serviceSpecialist = this.admissionInfo.serviceSpecialist;
      }

      if (this.admissionInfo.locationFiscalYearId) {
        await this.getSpecialists();
      }
      this.getSpecialistDetail();

      this.treatmentYearsOrMonthsValue = data.months || data.years;

      const treatmentSelected = data.months ? 2 : data.years ? 1 : null;

      this.treatmentOptions.forEach(item => {
        item.check = treatmentSelected == item.id;
      });

      this.treatmentYearsOrMonths = this.treatmentOptions.find(x => x.check);

      this.studentFactorsOptions = this.mapFactors(data.factors);
      this.studentFactorsOthersOptions = this.mapFactors(data.otherFactors);
      // JSF: Added the custom field with an open textarea
      this.studentFactorsOthersOptions.push({
        name: "custom-factor-field",
        label: "Otro.",
        check: false,
        invalid: false
      });

      const { data: history } = await getServiceResultSummaryHistory({
        studentSieId: this.admissionInfo.studentSieId,
        studentServiceId: this.admissionInfo.studentServiceId,
        academicYearId: this.admissionInfo.academicYearId
      });
      this.model.summaryHistory = history;

      this.model.certifySummary = data.certifySummary;
      if (data.specialistRecommendationAccepted !== null) {
        this.specialistRecommendationOptions.forEach(
          o => (o.check = o.value == data.specialistRecommendationAccepted)
        );

        this.hasStoredRecommendationValue = true;

        this.specialistRecommendation = this.specialistRecommendationOptions.find(
          o => o.check
        );
        this.originalSpecialistRecommendation = this.specialistRecommendationOptions.find(
          o => o.check
        );
      }

      let {
        data: service
      } = await generalService.getAnalysisServiceByPeiAndDisciplineType(
        this.peiId,
        this.admissionDisciplineTypeId
      );
      if (service) {
        this.peiAnalysisService = service;
        this.referralJustificationComment = this.peiAnalysisService.therapyServiceReason;
        this.originalJustificationComment = this.peiAnalysisService.therapyServiceReason;
      }

      if (data.evaluation) {
        this.model.evaluation = {
          diagnosis: data.evaluation.diagnosis,
          isFirstEvaluation: data.evaluation.isFirstEvaluation,
          id: data.evaluation.id,
          date: data.evaluation.date
        };
      }

      if (data.reevaluations && data.reevaluations.length > 0) {
        this.model.reEvaluations = [];
        data.reevaluations.forEach(item => {
          this.model.reEvaluations.push({
            id: item.id,
            diagnosis: item.diagnosis,
            date: this.formatDate(item.date),
            confirmedDate: item.confirmedDate
          });
        });
      }

      if (data.strengths) {
        data.strengths.forEach(item => {
          this.catalog.addedStrengths.push({
            id: item.id,
            name: item.name,
            highlight: true
          });
        });

        this.model.strengths = this.catalog.addedStrengths;
      }

      if (data.specialistSignature) {
        this.model.specialistSignature.push({
          id: data.specialistSignature.id,
          name: data.specialistSignature.name,
          signed: true,
          email: data.specialistSignature.email,
          phone: data.specialistSignature.phone,
          role: {
            id: data.specialistSignature.role.id,
            name: data.specialistSignature.role.name
          }
        });
      }

      this.catalog.recomendedTermChecks =
        data.questions.sections[0].content[1].questions[1];

      const answers = data.questions.sections[0].answers;

      this.catalog.recomendedTermChecks.answers.forEach(item => {
        item.isSelected =
          answers.find(x => x.questionId == 751 && x.answerId == item.id) !=
          null;
      });

      this.catalog.recoveryStudentCapacityChecks =
        data.questions.sections[0].content[1].questions[2];

      this.catalog.recoveryStudentCapacityChecks.answers.forEach(item => {
        item.isSelected =
          answers.find(x => x.questionId == 752 && x.answerId == item.id) !=
          null;
      });

      this.requiresContinueServiceOptions.forEach(item => {
        item.check =
          answers.find(x => x.questionId == 750 && x.answerId == item.id) !=
          null;
      });

      this.requiresContinueService = this.requiresContinueServiceOptions.find(
        x => x.check
      );

      this.recomendedTermOptions.forEach(item => {
        item.check =
          answers.find(x => x.questionId == 751 && x.answerId == item.id) !=
          null;
      });

      this.recomendedTerm = this.recomendedTermOptions.find(x => x.check);

      this.recoveryStudentCapacityOptions.forEach(item => {
        item.check =
          answers.find(x => x.questionId == 752 && x.answerId == item.id) !=
          null;
      });

      this.recoveryStudentCapacity = this.recoveryStudentCapacityOptions.find(
        x => x.check
      );

      this.studentFactorsOptions.forEach(item => {
        item.check =
          answers.find(x => x.questionId == item.id && x.answerId == 138) !=
          null;
      });

      this.studentFactors = this.studentFactorsOptions.find(x => x.check);

      this.studentFactorsOthersOptions.forEach(item => {
        item.check =
          answers.find(x => x.questionId == item.id && x.answerId == 138) !=
          null;
      });

      this.studentFactorsOther = this.studentFactorsOthersOptions.find(
        x => x.check
      );

      if (data.resumeType) {
        this.interventionResumeOptions.forEach(item => {
          item.check = data.resumeType == item.id;
        });

        this.model.resumeType = this.interventionResumeOptions.find(
          x => x.check
        );
      }
      this.model.resume = data.resume;

      if (data.analysis) {
        this.model.analysis = data.analysis;
        this.yearsOrMonthsValue = data.analysis.months || data.analysis.years;

        const selected = data.analysis.months
          ? 2
          : data.analysis.years
          ? 1
          : null;

        this.recommendedOptions.forEach(item => {
          item.check = selected == item.id;
        });

        this.yearsOrMonths = this.recommendedOptions.find(x => x.check);

        this.model.analysis.frequencyTypeId = this.catalog.frequencyTypes.find(
          x => x.id == data.analysis.frequencyTypeId
        );

        this.model.analysis.frequencyTypeCheck = !!this.model.analysis
          .frequencyTypeId;

        this.model.analysis.serviceModalityId = this.catalog.serviceModalities.find(
          x => x.id == data.analysis.serviceModalityId
        );

        this.model.analysis.serviceModalityCheck = !!this.model.analysis
          .serviceModalityId;

        this.model.analysis.periodTypeId = this.catalog.periods.find(
          x => x.id == data.analysis.periodTypeId
        );

        this.model.analysis.periodCheck = !!this.model.analysis.periodTypeId;
      } else {
        this.yearsOrMonths = null;
      }

      if (data.goals) {
        data.goals.forEach(item => {
          if (!this.model.goals.some(g => g.id === item.id)) {
            this.model.goals.push({
              id: item.id,
              academicYear: this.catalog.academicYears.find(
                x => x.id == item.academicYearId
              ),
              executionLevelArchieved: this.catalog.executionLevelArchieved.find(
                x => x.id == item.executionLevelArchievedId
              ),
              executionLevelPercent: item.percent,
              selectedGoal: { id: 1, name: item.goal }
            });
          }
        });
      }

      this.setFactorsCheckBox(answers, data.otherFactor);

      this.model.id = data.id;
      // this.model.isSubmitted = data.isSubmitted; - Currently not needing edition; US72335
      this.continuityServiceComment = data.continuityServiceComment;
    },
    mapFactors(factors) {
      return factors.map(factor => ({
        id: factor.id,
        name: factor.id.toString(),
        value: factor.id,
        label: factor.name,
        check: false,
        invalid: false
      }));
    },
    setFactorsCheckBox(answers, otherFactorText) {
      answers.forEach(answer => {
        const factor = this.studentFactorsOptions.find(
          x => x.id === answer.questionId
        );
        if (factor) {
          factor.check = true;
        }

        const otherFactor = this.studentFactorsOthersOptions.find(
          x => x.id === answer.questionId
        );
        if (otherFactor) {
          otherFactor.check = true;
          this.setOtherFactorCheckbox(true);
        }
      });
      otherFactorText = otherFactorText ?? "";
      if (otherFactorText.length > 0) {
        this.setOtherFactorCheckbox(true);

        const otherFactorOpenField = this.studentFactorsOthersOptions.find(
          x => x.name === "custom-factor-field" && !x.check
        );
        if (otherFactorOpenField) otherFactorOpenField.check = true;
        this.model.otherFactor = otherFactorText;
      }
    },
    setOtherFactorCheckbox(newValue) {
      // JSF: checbox for other section -> check it as true.
      const showOtherSection = this.studentFactorsOptions.find(
        x => x.id === this.factorQuestionIdNamedOther && !x.check
      );
      // JSF: Mark the checkboxes for Section Factors.
      if (showOtherSection) showOtherSection.check = newValue;
    },
    isObject(o) {
      return o instanceof Object && o.constructor === Object;
    },
    async certifyResultSummary() {
      await certifyServiceResultSummary({
        programServiceAreaId: Number(this.programServiceAreaId),
        summaryResultId: this.model.id,
        certified: this.model.certifySummary
      })
        .then(() =>
          this.ShowToast(
            "Proceso Exitoso",
            "La información ha sido guardada exitosamente.",
            "success"
          )
        )
        .catch(() =>
          this.ShowToast("Atención", "Ocurrio un problema inesperado", "error")
        );
    },
    onSpecialistRecommendationChange(value) {
      this.specialistRecommendation = value;
    },
    async getResultSummaryForRP() {
      // avoid calling the api again if already has been called.
      if (this.model.certifySummary !== null) return;

      const disciplineId =
        this.admissionDisciplineTypeId ?? this.disciplineTypeId;
      const payload = {
        programServiceAreaId: Number(this.programServiceAreaId),
        disciplineTypeId: disciplineId,
        peiId: this.peiId
      };
      const { data } = await getResultSummaryForRP(payload);

      this.model.certifySummary = data.certified;

      // set the specialist recommendation (yes or not) option
      const specialistRadioBtn = this.specialistRecommendationOptions.find(
        o => o.value == data.accepted
      );
      specialistRadioBtn.check = true;
      this.specialistRecommendation = specialistRadioBtn;
      
      // then set the comment
      if (!specialistRadioBtn.value) {
        this.referralJustificationComment = data.reason;
      }
    },
    async save() {
      const isValid = await this.$refs.resultSummaryConfirmation.validate();
      if (!isValid) {
        this.ShowToast(
          "Atención",
          "Favor de completar la información solicitada.",
          "error"
        );

        return isValid;
      }

      if (!this.continueServiceRecommended) {
        if (
          this.specialistRecommendationAccepted &&
          !this.showResultSummaryDocument
        ) {
          const disciplineName =
            this.admissionInfo?.disciplineName ?? this.disciplineName;
          let confirmation = await this.ShowCancelModal(
            "Atención",
            `¿Está seguro que desea realizar el alta de la Terapia - ${disciplineName}?`,
            "Si",
            "No"
          );
          if (!confirmation) return !isValid;
        }
      }
      const disciplineId =
        this.admissionDisciplineTypeId ?? this.disciplineTypeId;
      const payload = {
        resultSummaryId: this.model.id,
        programServiceAreaId: Number(this.programServiceAreaId),
        admissionId: this.admissionId,
        studentSieId: this.studentSie,
        studentServiceId: this.admissionInfo.studentServiceId,
        disciplineTypeId: disciplineId,
        peiId: this.peiId,
        academicYearId: this.academicYearId,
        continueServiceRecommended: this.continueServiceRecommended,
        specialistRecommendationAccepted: this.specialistRecommendationAccepted,
        referralJustificationComment: this.referralJustificationComment,
        hasResultSummaryDocument: false
      };

      if (this.showResultSummaryDocument) {
        // remove null or undefined properties
        Object.keys(payload).forEach(key => {
          if (payload[key] === null || payload[key] === undefined) {
            delete payload[key];
          }
        });

        payload.hasResultSummaryDocument = true;
      }

      try {
        await saveResultSummarySpecialistRecommendation(payload);
        if (
          !this.continueServiceRecommended &&
          this.specialistRecommendationAccepted
        ) {
          this.$root.$router.push("/pei/detail?tab=Tab4");
        } else {
          this.ShowToast(
            "Proceso Exitoso",
            "La información ha sido guardada exitosamente.",
            "success"
          );
        }
      } catch (error) {
        if (error && error.response && error.response.data) {
          this.ShowToast("", error.response.data.message, "error");
        } else {
          this.ShowToast(
            "",
            "Ha ocurrido un error mientras se procesaba la información.",
            "error"
          );
        }

        return !isValid;
      }

      this.$emit("pending-changes", false);
      return isValid;
    },
    printResultSummaryDocument() {
      DocumentsService.Download(this.resultSummaryDocument);
    },
    alertHandler(errorMsg = null) {
      if (errorMsg) {
        this.ShowToast("Atención", errorMsg, "error");
        return;
      }
      this.ShowToast(
        "Proceso Exitoso",
        "La información ha sido guardada exitosamente.",
        "success"
      );
    },
    formatDate(date) {
      if (date) {
        return moment(date)
          .locale("es-PR")
          .format("DD/MMM/YYYY");
      }
    },
    async loadData() {
      if (
        this.admissionStatusId &&
        this.admissionStatusId == admissionStatus.AprobadoRemedioProvisional &&
        this.resultSummaryDocument
      ) {
        this.showResultSummaryDocument = true;
        await this.getResultSummaryForRP();
        // change pending-changes to false due to the above methods getResultSummaryForRP throw the watch on pending-changes
        // but we are only loading the data, so the user hasn't changed anything yet.
        this.$emit("pending-changes", false);
      } else if (this.hasResultSummary) {
        await this.getServiceModalities();
        await this.getPeriods();
        await this.getFrequencyType();

        await Promise.all([
          this.getAdmissionInfo(),
          this.getCodeExecutionLevelArchieved(),
          this.getAcademicYearsToType()
        ]);

        await this.getEvaluationDates();

        if (
          this.validator.hasResultSummarySectionError(this.programServiceAreaId)
        ) {
          this.$refs.resultSummary.validate();
        }
      }
    }
  },
  async mounted() {
    await this.loadData();
  }
};
</script>

<style scoped>
.border-box {
  border: 1px solid #e2e2e2;
  border-radius: 5px 5px 0px 0px;
  padding: 20px;
  width: 100%;
}

.border-box.gray {
  border-radius: 0px 0px 5px 0px;
  background: #f2f2f2 0% 0% no-repeat padding-box;
  border-top: none;
  padding: 20px 0;
}

.border-box.gray.error {
  background: #ffebeb 0% 0% no-repeat padding-box;
  border: 1px solid #c9545e;
}

.invalid {
  text-align: left;
  font: Lato, Regular;
  letter-spacing: 0;
  color: #ff5b5c;
  margin-bottom: 5px;
  font-weight: normal;
}
</style>
